import { useState, useMemo, useEffect } from "react";
import { PageTitle } from "@/components/PageHeader";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useDash } from "./ctx";
import { makeVocab } from "@/lib/vocab";
import { CORE_MODULES, ADDON_MODULES, moduleLabel, defaultCoreState, defaultAddonState } from "./wizardModules";
import { PulseChip } from "@/components/pulse/PulseChip";
import { usePulseBadge } from "@/components/pulse/usePulseBadge";
import { IndustryPicker } from "@/components/IndustryPicker";
import { BuyNumberPanel } from "@/components/BuyNumberPanel";
import { toast } from "sonner";
import { PlatformCapacityStrip } from "@/components/PlatformCapacityStrip";
import { InviteFlyerDialog } from "@/components/InviteFlyer";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Building2, Plus, X, MessagesSquare, Phone, AlertTriangle, ClipboardList, Users, ArrowRight, Headset, ShieldAlert, MessageSquareText, SlidersHorizontal, Check, Search, Flag, UserRound, Megaphone, Sparkles, Trash2, LayoutGrid, List, ChevronDown, Landmark } from "lucide-react";
import { NoAccess, PLATFORM_ROLES } from "@/components/NoAccess";
import { DataHygieneCard } from "@/components/DataHygieneCard";
import { CommunityMark } from "@/components/CommunityMark";
import { ManagedBy, managerOptions, matchesManager, managerSortKey } from "@/components/ManagedBy";

const disclosureFor = (wv) => `Heads up: you're chatting with an automated ${wv.lc("org")} assistant. Messages may be recorded and stored by the ${wv.lc("org")} for support and record-keeping. Msg & data rates may apply. Reply STOP to opt out, HELP for info. For emergencies, call 911.`;
const DEFAULT_DISCLOSURE = disclosureFor(makeVocab(null));


function ModulePill({ on, icon: Icon, label }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full ${on ? "bg-emerald-50 text-emerald-700" : "bg-sand-100 text-muted-foreground"}`}>
      <Icon className="h-3 w-3" strokeWidth={1.75} /> {label}
    </span>
  );
}

function Kpi({ icon: Icon, value, label, alert }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-ink/40"><Icon className={`h-3.5 w-3.5 ${alert ? "text-redline-500" : ""}`} strokeWidth={1.75} /></div>
      <div className={`font-heading text-2xl font-extrabold tracking-tight mt-0.5 ${alert ? "text-redline-600" : "text-ink"}`}>{value}</div>
      <div className="text-xs text-ink/50">{label}</div>
    </div>
  );
}

const TWILIO_STATE = {
  up: { dot: "bg-emerald-500", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Live" },
  misconfigured: { dot: "bg-amber-500", cls: "bg-amber-50 text-amber-700 border-amber-200", label: "Check webhook" },
  down: { dot: "bg-danger-500", cls: "bg-red-50 text-danger-700 border-red-200", label: "Down" },
  unknown: { dot: "bg-ink/30", cls: "bg-sand-100 text-ink/50 border-sand-200", label: "Unknown" },
};

function TwilioStatusPill({ status, tid }) {
  if (!status || status.state === "no_number") return null;
  const s = TWILIO_STATE[status.state] || TWILIO_STATE.unknown;
  const title = status.state === "up" ? "Texting number is live — SMS + Voice webhooks wired correctly"
    : status.state === "misconfigured" ? `Only one webhook is wired (SMS ${status.sms_ok ? "OK" : "missing"}, Voice ${status.voice_ok ? "OK" : "missing"}). Re-assign the number to re-wire both.`
    : status.state === "down" ? (status.reason || "Number not reachable — webhooks not pointing to Ask Redline. Re-assign the number to fix.")
    : (status.reason || "Live status unavailable (Twilio not configured).");
  return (
    <span data-testid={`twilio-status-${tid}`} title={title}
      className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide inline-flex items-center gap-1 ${s.cls}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot} ${status.state === "up" ? "animate-pulse" : ""}`} /> {s.label}
    </span>
  );
}

const BRAND_PRESETS = [
  ["#D32F2F", "Redline red"], ["#1B7F4B", "Forest green"], ["#1D5FBF", "Lake blue"],
  ["#0D9488", "Teal"], ["#7C3AED", "Violet"], ["#B45309", "Amber"], ["#BE185D", "Berry"],
];
const EMPTY_LOOK = { unit_count: "", brand_color: "", logo_path: "", logo_preview: "" };

function ModuleGrid({ title, hint, items, state, onChange, wv, prefix }) {
  const on = items.filter((m) => state[m.key]).length;
  return (
    <div data-testid={`${prefix}-group`}>
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-ink/50">{title} <span className="text-ink/35 normal-case tracking-normal font-semibold">· {on}/{items.length} on</span></div>
        {hint && <div className="text-[11px] text-ink/40">{hint}</div>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {items.map((m) => {
          const checked = !!state[m.key];
          return (
            <button key={m.key} type="button" data-testid={`${prefix}-${m.key}`} aria-pressed={checked} onClick={() => onChange(m.key, !checked)}
              className={`flex items-start gap-2.5 rounded-xl border px-3 py-2.5 text-left transition-colors ${checked ? "border-ink bg-white" : "border-sand-200 bg-sand-50/60 opacity-70 hover:opacity-100"}`}>
              <m.icon className={`h-4 w-4 mt-0.5 shrink-0 ${checked ? "text-redline-500" : "text-ink/35"}`} strokeWidth={2} />
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-semibold text-ink truncate">{moduleLabel(m, wv)}</span>
                <span className="block text-[11px] text-ink/55 leading-snug">{m.desc}</span>
                {m.note && <span className="block text-[11px] text-ink/40 leading-snug mt-0.5 italic">{m.note}</span>}
              </span>
              <Switch checked={checked} onCheckedChange={(v) => onChange(m.key, v)} onClick={(e) => e.stopPropagation()} className="mt-0.5 shrink-0" />
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Communities() {
  const { tenants, enterCommunity, refreshTenants, role } = useDash();
  const [pulseSnaps, reloadPulse] = usePulseBadge();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [poster, setPoster] = useState(null);
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [basics, setBasics] = useState({ name: "", persona_name: "", ai_model: "auto" });
  const [numSel, setNumSel] = useState({ phone_number: "", number_sid: "", mode: "pool" });
  const [numbers, setNumbers] = useState(null);
  const [numCfg, setNumCfg] = useState(true);
  const [look, setLook] = useState(EMPTY_LOOK);
  const [logoBusy, setLogoBusy] = useState(false);
  const [features, setFeatures] = useState(defaultCoreState(null));
  const [addons, setAddons] = useState(defaultAddonState(null));
  const [disc, setDisc] = useState({ enabled: true, text: DEFAULT_DISCLOSURE });
  const [guard, setGuard] = useState({ blocked_topics: "", blocked_words: "" });
  const [companies, setCompanies] = useState([]);
  const [packs, setPacks] = useState([]);
  const [industry, setIndustry] = useState("hoa");
  // Wizard wording follows the chosen pack (Community → Company, HOA manager → Office manager…).
  const pack = packs.find((p) => p.id === industry) || null;
  const wv = useMemo(() => makeVocab(pack ? { industry_pack: { is_default: !!pack.is_default }, vocab: pack.vocab_preview } : null), [pack]);
  useEffect(() => {
    setDisc((d) => (d.text === DEFAULT_DISCLOSURE || /^Heads up: you're chatting with an automated .* assistant\./.test(d.text) ? { ...d, text: disclosureFor(wv) } : d));
  }, [wv]);
  useEffect(() => { if (open) { setFeatures(defaultCoreState(pack)); setAddons(defaultAddonState(pack)); } }, [pack?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [invites, setInvites] = useState([{ email: "", name: "", role: "hoa_manager" }]);
  const [q, setQ] = useState("");
  const [manager, setManager] = useState("all");
  const managers = useMemo(() => managerOptions(tenants), [tenants]);
  const [sortBy, setSortBy] = useState("attention");
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [pendingOnly, setPendingOnly] = useState(false);
  const [regsOnly, setRegsOnly] = useState(false);
  const [twilioStatus, setTwilioStatus] = useState({});
  const [delTarget, setDelTarget] = useState(null);
  const [delText, setDelText] = useState("");
  const [delRelease, setDelRelease] = useState(false);   // hand the number back to Twilio?
  const [deleting, setDeleting] = useState(false);
  // Card vs list view — persisted so the portfolio stays the way you left it.
  const [view, setViewState] = useState(() => {
    try { return localStorage.getItem("askredline.communitiesView") || "cards"; } catch { return "cards"; }
  });
  const setView = (v) => {
    setViewState(v);
    try { localStorage.setItem("askredline.communitiesView", v); } catch { /* ignore */ }
  };
  // Cards start compact; the chevron reveals board members, modules & secondary actions.
  const [expandedCards, setExpandedCards] = useState({});
  const toggleCard = (id) => setExpandedCards((m) => ({ ...m, [id]: !m[id] }));

  const doDelete = async () => {
    if (!delTarget || delText.trim() !== delTarget.name) return;
    setDeleting(true);
    try {
      const r = await api.delete(`/tenants/${delTarget.id}${delTarget.phone_number && delRelease ? "?release_number=true" : ""}`);
      const num = r.data?.number;
      toast.success(`Deleted "${delTarget.name}"`
        + (num ? (num.released ? ` · ${num.phone_number || delTarget.phone_number} released back to Twilio${num.dry_run ? " (simulated in preview)" : ""}`
          : num.kept ? ` · ${delTarget.phone_number} kept on the account` : "") : ""));
      if (num && delRelease && !num.released) toast.warning(num.error || "The number could not be released — release it from Platform → Numbers.");
      setDelTarget(null); setDelText(""); setDelRelease(false);
      await refreshTenants();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete community");
    } finally { setDeleting(false); }
  };

  useEffect(() => {
    let alive = true;
    const fetchStatus = () => api.get("/admin/twilio/status")
      .then((r) => { if (alive) setTwilioStatus(r.data.communities || {}); })
      .catch(() => {});
    fetchStatus();
    const iv = setInterval(fetchStatus, 60000); // live refresh every 60s
    return () => { alive = false; clearInterval(iv); };
  }, []);

  const totals = useMemo(() => ({
    communities: tenants.length,
    residents: tenants.reduce((n, t) => n + (t.stats?.households || 0), 0),
    // "flagged" = communities needing attention (escalated convs + urgent open requests)
    flaggedCommunities: tenants.filter((t) => ((t.stats?.escalations || 0) + (t.stats?.urgent_requests || 0)) > 0).length,
    pendingRegs: tenants.reduce((n, t) => n + (t.stats?.pending_registrations || 0), 0),
    pending: tenants.filter((t) => t.status === "pending_activation").length,
    rcsLive: tenants.filter((t) => (t.rcs_status || (t.rcs_sender_id ? "approved" : "")) === "approved").length,
    rcsSubmitted: tenants.filter((t) => t.rcs_status === "submitted").length,
    rcsReady: tenants.filter((t) => t.rcs_status === "ready").length,
    rcsDraft: tenants.filter((t) => t.rcs_status === "draft").length,
  }), [tenants]);
  const liveNumbers = useMemo(() => Object.values(twilioStatus).filter((s) => s?.state === "up").length, [twilioStatus]);

  const list = useMemo(() => {
    let arr = tenants.filter((t) => (!flaggedOnly || ((t.stats?.escalations || 0) + (t.stats?.urgent_requests || 0)) > 0)
      && (!pendingOnly || t.status === "pending_activation")
      && (!regsOnly || (t.stats?.pending_registrations || 0) > 0)
      && matchesManager(t, manager));
    if (q.trim()) {
      const s = q.toLowerCase();
      arr = arr.filter((t) => (t.name || "").toLowerCase().includes(s) || (t.persona_name || "").toLowerCase().includes(s));
    }
    const sorters = {
      attention: (a, b) => ((b.stats?.escalations || 0) + (b.stats?.urgent_requests || 0)) - ((a.stats?.escalations || 0) + (a.stats?.urgent_requests || 0)) || (b.stats?.conversations || 0) - (a.stats?.conversations || 0),
      name: (a, b) => (a.name || "").localeCompare(b.name || ""),
      chats: (a, b) => (b.stats?.conversations || 0) - (a.stats?.conversations || 0),
      manager: (a, b) => managerSortKey(a).localeCompare(managerSortKey(b)) || (a.name || "").localeCompare(b.name || ""),
    };
    return [...arr].sort(sorters[sortBy] || sorters.attention);
  }, [tenants, q, sortBy, flaggedOnly, pendingOnly, regsOnly, manager]);

  const cycleUnit = async (t) => {
    const next = (t.unit_label || "Homes") === "Homes" ? "Units" : "Homes";
    try { await api.patch(`/tenants/${t.id}/unit-label`, { unit_label: next }); await refreshTenants(); }
    catch (e) { toast.error("Couldn't update label"); }
  };

  if (!PLATFORM_ROLES.includes(role)) return <NoAccess page="Communities" />;

  const startWizard = () => {
    setStep(1); setBasics({ name: "", persona_name: "", company_id: "", request_code: "", ai_model: "auto" });
    setInvites([{ email: "", name: "", role: "hoa_manager" }]);
    setNumSel({ phone_number: "", number_sid: "", mode: "pool" });
    setNumbers(null);
    setFeatures(defaultCoreState(null));
    setAddons(defaultAddonState(null));
    setDisc({ enabled: true, text: DEFAULT_DISCLOSURE });
    setGuard({ blocked_topics: "", blocked_words: "" });
    setLook(EMPTY_LOOK);
    setIndustry("hoa");
    setOpen(true);
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => setCompanies([]));
    api.get("/industries").then((r) => setPacks(r.data || [])).catch(() => setPacks([]));
    api.get("/admin/twilio/numbers", { params: { base_url: window.location.origin } })
      .then((r) => { setNumbers(r.data.numbers || []); setNumCfg(r.data.configured !== false); })
      .catch(() => { setNumbers([]); setNumCfg(false); });
  };

  const uploadWizardLogo = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error("Keep the logo under 4 MB"); return; }
    const fd = new FormData();
    fd.append("file", f);
    setLogoBusy(true);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLook((l) => ({ ...l, logo_path: r.data.path, logo_preview: URL.createObjectURL(f) }));
    } catch { toast.error("Upload failed"); }
    finally { setLogoBusy(false); }
  };

  const finish = async () => {
    if (!basics.name.trim()) { toast.error("Community name required"); setStep(1); return; }
    setSaving(true);
    try {
      const r = await api.post("/tenants", { ...basics, industry });
      const id = r.data.id;
      await api.put(`/tenants/${id}/features`, features);
      try {
        // `copilot` lives in /config and `checklists` has its own config doc — neither is an entitlement.
        const ent = Object.fromEntries(ADDON_MODULES.filter((m) => !["copilot", "checklists"].includes(m.key)).map((m) => [m.key, !!addons[m.key]]));
        await api.put(`/tenants/${id}/entitlements`, { entitlements: ent });
      } catch (e) { toast.warning("Community created, but add-ons couldn't be saved — set them in Modules."); }
      if (addons.checklists) {
        try {
          await api.put(`/tenants/${id}/checklists/config`, { enabled: true });
        } catch (e) { toast.warning("Community created, but Checklists couldn't be turned on — flip it on in Modules."); }
      }
      const homes = parseInt(look.unit_count, 10);
      if (homes > 0 || look.brand_color || look.logo_path) {
        try {
          await api.put(`/tenants/${id}/community-profile`, {
            unit_count: homes > 0 ? homes : null, brand_color: look.brand_color || "", logo_path: look.logo_path || "",
          });
        } catch (e) { toast.warning("Community created, but the look & feel couldn't be saved — finish it in Community Profile."); }
      }
      await api.put(`/tenants/${id}/config`, {
        request_code: (basics.request_code || "").toUpperCase().replace(/[^A-Z]/g, "").slice(0, 5),
        ai_model: basics.ai_model || "auto",
        copilot_enabled: !!addons.copilot,
        disclosure: disc,
        blocked_topics: guard.blocked_topics.split(/[,\n]/).map((x) => x.trim()).filter(Boolean),
        blocked_words: guard.blocked_words.split(/[,\n]/).map((x) => x.trim()).filter(Boolean),
      });
      if (numSel.phone_number.trim()) {
        try {
          await api.post("/admin/twilio/assign-number", {
            tenant_id: id, phone_number: numSel.phone_number.trim(),
            number_sid: numSel.number_sid || "", wire_webhook: !!numSel.number_sid,
            base_url: window.location.origin,
          });
        } catch (e) { toast.warning("Community created, but the number couldn't be wired — assign it later in Numbers."); }
      }
      const toInvite = invites.filter((i) => i.email.trim());
      let invited = 0;
      for (const i of toInvite) {
        try {
          await api.post("/admins/invite", { email: i.email.trim(), name: i.name.trim(), role: i.role, tenant_ids: [id] });
          invited++;
        } catch (e) { toast.warning(`Couldn't invite ${i.email}: ${e.response?.data?.detail || "error"}`); }
      }
      toast.success(`Community created${invited ? ` · ${invited} invite${invited === 1 ? "" : "s"} sent` : ""}`);
      setOpen(false);
      await refreshTenants();
      enterCommunity(id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <PageTitle title="Communities" about="Your whole portfolio at a glance — jump into any community, or configure it." />
        </div>
        <button data-testid="add-community-btn" onClick={startWizard} className="rounded-full bg-redline-500 text-white font-semibold px-5 py-2.5 flex items-center gap-2 hover:bg-redline-600 transition-colors active:scale-95 shrink-0">
          <Plus className="h-4 w-4" strokeWidth={2} /> New community
        </button>
      </div>

      {role === "super_admin" && <DataHygieneCard />}

      {/* Platform capacity — voice gauge + SMS throughput, collapsed behind one strip */}
      <PlatformCapacityStrip />

      {/* Portfolio rollup — compact strip of genuinely useful, clickable portfolio stats */}
      <div data-testid="portfolio-rollup" className="bg-white rounded-2xl border border-sand-200 px-5 py-3 flex flex-wrap items-center gap-x-7 gap-y-2">
        <div className="flex items-baseline gap-1.5">
          <span className="font-heading text-xl font-extrabold tracking-tight text-ink">{totals.communities}</span>
          <span className="text-xs text-ink/50 font-medium">Communities</span>
        </div>
        <button onClick={() => navigate("/dashboard/numbers")} data-testid="rollup-live-numbers" title="Open Numbers"
          className="flex items-baseline gap-1.5 rounded-full px-3 py-1 -my-1 hover:bg-sand-50 transition-colors">
          <span className={`font-heading text-xl font-extrabold tracking-tight ${liveNumbers > 0 ? "text-emerald-700" : "text-ink/70"}`}>{liveNumbers}</span>
          <span className="text-xs text-ink/50 font-medium">Live number{liveNumbers === 1 ? "" : "s"}</span>
        </button>
        <div className="flex items-baseline gap-1.5">
          <span className="font-heading text-xl font-extrabold tracking-tight text-ink/70">{totals.residents}</span>
          <span className="text-xs text-ink/50 font-medium">Homes on file</span>
        </div>
        {totals.pendingRegs > 0 && (
          <button onClick={() => setRegsOnly((v) => !v)} data-testid="rollup-pending-regs"
            title={regsOnly ? "Show all communities" : "Show only communities with residents awaiting approval"}
            className={`flex items-baseline gap-1.5 rounded-full px-3 py-1 -my-1 transition-colors ${regsOnly ? "bg-amber-500" : "bg-amber-50 hover:bg-amber-100"}`}>
            <span className={`font-heading text-xl font-extrabold tracking-tight ${regsOnly ? "text-white" : "text-amber-700"}`}>{totals.pendingRegs}</span>
            <span className={`text-xs font-medium ${regsOnly ? "text-white" : "text-amber-700"}`}>Pending sign-up{totals.pendingRegs === 1 ? "" : "s"}</span>
          </button>
        )}
        {totals.flaggedCommunities > 0 && (
          <button onClick={() => setFlaggedOnly((v) => !v)} data-testid="rollup-attention" title={flaggedOnly ? "Show all communities" : "Show only flagged communities"}
            className={`flex items-baseline gap-1.5 rounded-full px-3 py-1 -my-1 transition-colors ${flaggedOnly ? "bg-redline-500" : "bg-redline-50 hover:bg-redline-100"}`}>
            <span className={`font-heading text-xl font-extrabold tracking-tight flex items-center gap-1.5 ${flaggedOnly ? "text-white" : "text-redline-600"}`}>
              {totals.flaggedCommunities}<Flag className="h-3.5 w-3.5" />
            </span>
            <span className={`text-xs font-medium ${flaggedOnly ? "text-white" : "text-redline-600"}`}>Need{totals.flaggedCommunities === 1 ? "s" : ""} attention</span>
          </button>
        )}
      </div>

      {/* RCS rollout tracker — hidden; RCS is managed in the Twilio Console */}
      {false && (totals.rcsLive + totals.rcsSubmitted + totals.rcsReady + totals.rcsDraft) > 0 && (
        <div data-testid="rcs-tracker" className="rounded-2xl border border-sand-200 bg-white p-4 flex flex-wrap items-center gap-x-6 gap-y-2">
          <div className="flex items-center gap-1.5 font-heading font-bold text-sm"><Sparkles className="h-4 w-4 text-redline-500" strokeWidth={1.5} /> RCS rollout</div>
          {[["Live", totals.rcsLive, "text-emerald-700"], ["Submitted", totals.rcsSubmitted, "text-blue-700"], ["Ready", totals.rcsReady, "text-amber-700"], ["Draft", totals.rcsDraft, "text-ink/50"]].map(([lbl, n, cls]) => (
            <div key={lbl} className="flex items-baseline gap-1.5">
              <span className={`font-heading text-xl font-extrabold ${cls}`}>{n}</span>
              <span className="text-xs text-muted-foreground">{lbl}</span>
            </div>
          ))}
          <span className="text-xs text-muted-foreground ml-auto">{tenants.length - (totals.rcsLive + totals.rcsSubmitted + totals.rcsReady + totals.rcsDraft)} on standard SMS</span>
        </div>
      )}

      {/* Sticky toolbar */}
      <div className="sticky top-0 z-30 -mx-4 lg:-mx-8 px-4 lg:px-8 py-3 bg-sand-50/80 backdrop-blur-xl flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink/40" />
          <input data-testid="community-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search communities…"
            className="w-full rounded-full bg-white border border-sand-200 pl-9 pr-4 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-redline-500 focus-visible:ring-offset-2 focus-visible:ring-offset-sand-50" />
        </div>
        <select data-testid="community-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)}
          className="rounded-full bg-white border border-sand-200 px-4 py-2 text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-redline-500">
          <option value="attention">Sort: Needs attention</option>
          <option value="chats">Sort: Most conversations</option>
          <option value="name">Sort: Name (A–Z)</option>
          <option value="manager">Sort: Manager</option>
        </select>
        <select data-testid="community-manager-filter" value={manager} onChange={(e) => setManager(e.target.value)}
          className={`rounded-full bg-white border px-4 py-2 text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-redline-500 ${manager !== "all" ? "border-ink text-ink" : "border-sand-200"}`}>
          <option value="all">Manager: All</option>
          {managers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          <option value="none">Unassigned</option>
        </select>
        <button data-testid="flagged-only-toggle" onClick={() => setFlaggedOnly((v) => !v)}
          title="Show only communities with escalated conversations or urgent open requests"
          className={`rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${flaggedOnly ? "bg-redline-500 text-white border-redline-500" : "bg-white text-ink/70 border-sand-200 hover:border-ink"}`}>
          Flagged only{totals.flaggedCommunities ? ` · ${totals.flaggedCommunities}` : ""}
        </button>
        <button data-testid="pending-only-toggle" onClick={() => setPendingOnly((v) => !v)}
          className={`rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${pendingOnly ? "bg-amber-500 text-white border-amber-500" : "bg-white text-ink/70 border-sand-200 hover:border-ink"}`}>
          Awaiting number{totals.pending ? ` · ${totals.pending}` : ""}
        </button>
        <div className="flex rounded-full border border-sand-200 bg-white p-0.5" data-testid="view-toggle">
          <button data-testid="view-cards-btn" onClick={() => setView("cards")} title="Card view"
            className={`h-8 w-9 rounded-full flex items-center justify-center transition-colors ${view === "cards" ? "bg-ink text-white" : "text-ink/50 hover:text-ink"}`}>
            <LayoutGrid className="h-4 w-4" strokeWidth={1.75} />
          </button>
          <button data-testid="view-list-btn" onClick={() => setView("list")} title="List view"
            className={`h-8 w-9 rounded-full flex items-center justify-center transition-colors ${view === "list" ? "bg-ink text-white" : "text-ink/50 hover:text-ink"}`}>
            <List className="h-4 w-4" strokeWidth={1.75} />
          </button>
        </div>
      </div>

      {view === "list" ? (
        <div className="bg-white rounded-2xl border border-sand-200 divide-y divide-sand-100 overflow-hidden" data-testid="communities-list">
          {list.map((t) => {
            const flagged = (t.stats?.escalations || 0) + (t.stats?.urgent_requests || 0);
            const urgent = t.stats?.urgent_requests || 0;
            const unit = t.unit_label || "Homes";
            const vt = makeVocab(t);                 // this row's industry words (iter508)
            return (
              <div key={t.id} data-testid={`community-row-${t.id}`} className="group flex items-center gap-3 px-4 py-3 hover:bg-sand-50/70 transition-colors">
                <button onClick={() => enterCommunity(t.id)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                  <CommunityMark tenant={t} size={36} rounded="rounded-lg" flagged={flagged > 0} testid={`community-mark-${t.id}`} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-heading font-bold text-sm text-ink truncate">{t.name}</span>
                      {urgent > 0 && (
                        <span data-testid={`urgent-badge-${t.id}`} onClick={(e) => { e.stopPropagation(); enterCommunity(t.id, "/dashboard/actions"); }} title="Open urgent requests"
                          className="rounded-full bg-danger-600 text-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide shrink-0 hover:bg-danger-700 transition-colors cursor-pointer">{urgent} urgent</span>
                      )}
                      {t.status === "pending_activation" && (
                        <span data-testid={`pending-badge-${t.id}`} className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide shrink-0">Pending number</span>
                      )}
                      <TwilioStatusPill status={twilioStatus[t.id]} tid={t.id} />
                    </div>
                    <div className="text-xs text-ink/50 truncate mt-0.5">
                      <span className="font-semibold" style={{ color: t.brand?.color || "#e5342e" }}>{t.persona_name}</span>
                      {t.phone_number ? <span className="tabular-nums"> · {t.phone_number}</span> : " · No number yet"}
                    </div>
                    {/* iter195w — portfolio linkage on the list row so operators can see
                        at a glance whether a community is tied to a portfolio (and which
                        one), instead of having to open the portfolio Overview to find out. */}
                    <div data-testid={`community-portfolio-${t.id}`} className="text-[11px] mt-0.5 truncate flex items-center gap-2">
                      {t.company_name ? (
                        <span className="text-ink/45">in <span className="font-semibold text-ink/70">{t.company_name}</span></span>
                      ) : (
                        <span className="text-amber-700 font-semibold">Not in a portfolio</span>
                      )}
                      <span className="text-ink/25">·</span>
                      <ManagedBy tenant={t} testid={`community-manager-${t.id}`} />
                    </div>
                  </div>
                </button>
                <div className="hidden md:flex items-center gap-2 text-xs tabular-nums shrink-0">
                  <button data-testid={`stat-chats-${t.id}`} onClick={() => enterCommunity(t.id, "/dashboard/conversations")} title="Text conversations (all time)"
                    className="flex items-center gap-1 text-ink/50 w-16 justify-end rounded-full px-1.5 py-1 hover:bg-sand-100 hover:text-ink transition-colors">
                    <MessagesSquare className="h-3.5 w-3.5 text-ink/30" strokeWidth={1.75} /> {t.stats?.texts ?? t.stats?.conversations ?? 0}
                  </button>
                  <button data-testid={`stat-calls-${t.id}`} onClick={() => enterCommunity(t.id, "/dashboard/conversations?channel=voice")} title="Phone calls (all time)"
                    className="flex items-center gap-1 text-ink/50 w-14 justify-end rounded-full px-1.5 py-1 hover:bg-sand-100 hover:text-ink transition-colors">
                    <Phone className="h-3.5 w-3.5 text-ink/30" strokeWidth={1.75} /> {t.stats?.calls ?? 0}
                  </button>
                  <button data-testid={`stat-flagged-${t.id}`} onClick={() => enterCommunity(t.id, "/dashboard/conversations?status=escalated")} title="Open escalated conversations"
                    className={`flex items-center gap-1 w-14 justify-end rounded-full px-1.5 py-1 transition-colors ${flagged > 0 ? "text-redline-600 font-bold hover:bg-redline-50" : "text-ink/50 hover:bg-sand-100 hover:text-ink"}`}>
                    <AlertTriangle className={`h-3.5 w-3.5 ${flagged > 0 ? "text-danger-500" : "text-ink/30"}`} strokeWidth={1.75} /> {flagged}
                  </button>
                  <button data-testid={`stat-homes-${t.id}`} onClick={() => enterCommunity(t.id, "/dashboard/roster")} title={`Open ${vt.lcs("person")} (${unit})`}
                    className="flex items-center gap-1 text-ink/50 w-16 justify-end rounded-full px-1.5 py-1 hover:bg-sand-100 hover:text-ink transition-colors">
                    <Users className="h-3.5 w-3.5 text-ink/30" strokeWidth={1.75} /> {t.stats?.households || 0}
                  </button>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button data-testid={`invite-residents-${t.id}`} onClick={() => setPoster(t)} title={`Invite ${vt.lcs("person")} — QR poster & opt-in link`}
                    className="h-8 w-8 rounded-lg text-ink/35 hover:text-ink hover:bg-sand-100 flex items-center justify-center transition-colors">
                    <Users className="h-4 w-4" strokeWidth={1.75} />
                  </button>
                  <button data-testid={`configure-community-${t.id}`} onClick={() => enterCommunity(t.id, "/dashboard/settings?tab=service")} title="Configure"
                    className="h-8 w-8 rounded-lg text-ink/35 hover:text-ink hover:bg-sand-100 flex items-center justify-center transition-colors">
                    <SlidersHorizontal className="h-4 w-4" strokeWidth={1.75} />
                  </button>
                  {role === "super_admin" && (
                    <button data-testid={`delete-community-${t.id}`} onClick={() => { setDelTarget(t); setDelText(""); setDelRelease(false); }} title="Delete community"
                      className="h-8 w-8 rounded-lg text-ink/35 hover:text-redline-600 hover:bg-redline-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                    </button>
                  )}
                  <button data-testid={`manage-community-${t.id}`} onClick={() => enterCommunity(t.id)}
                    className="rounded-full bg-ink text-white hover:bg-redline-500 transition-colors font-semibold px-3.5 py-1.5 text-xs flex items-center gap-1">
                    Manage <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
                  </button>
                </div>
              </div>
            );
          })}
          {list.length === 0 && (
            <div className="text-center py-16 text-ink/40" data-testid="communities-empty">
              {tenants.length === 0 ? "No communities yet — create your first one."
                : flaggedOnly && !q.trim() && !pendingOnly && !regsOnly ? "All clear — no communities are flagged right now."
                : regsOnly && !q.trim() && !pendingOnly && !flaggedOnly ? "No residents are awaiting approval right now."
                : "No communities match your search / filter."}
            </div>
          )}
        </div>
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {list.map((t) => {
          const flagged = (t.stats?.escalations || 0) + (t.stats?.urgent_requests || 0);
          const urgent = t.stats?.urgent_requests || 0;
          const bms = t.board_members || [];
          const unit = t.unit_label || "Homes";
          const vt = makeVocab(t);                   // this card's industry words (iter508)
          const isOpen = !!expandedCards[t.id];
          return (
            <div key={t.id} data-testid={`community-card-${t.id}`}
              className="group flex flex-col relative h-full bg-white rounded-2xl border border-sand-200 shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_10px_36px_rgba(0,0,0,0.08)] transition-shadow duration-300 overflow-hidden">
              <div className="absolute top-0 inset-x-0 h-1" style={{ backgroundColor: flagged > 0 ? "#e5342e" : (t.brand?.color || "#191614") }} data-testid={`community-brand-bar-${t.id}`} />
              {role === "super_admin" && (
                <button data-testid={`delete-community-${t.id}`} onClick={(e) => { e.stopPropagation(); setDelTarget(t); setDelText(""); setDelRelease(false); }}
                  title="Delete community"
                  className="absolute top-3 right-3 z-20 h-7 w-7 rounded-lg bg-white/90 backdrop-blur border border-sand-200 text-ink/40 hover:text-redline-600 hover:border-redline-300 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                </button>
              )}

              {/* header */}
              <div className="p-4 flex items-start gap-3">
                <CommunityMark tenant={t} size={44} flagged={flagged > 0} testid={`community-mark-${t.id}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-start gap-2">
                    <h3 className="font-heading text-[15px] font-bold text-ink leading-snug line-clamp-2 flex-1">{t.name}</h3>
                    {flagged > 0 && <span className="mt-1.5 h-2 w-2 rounded-full bg-redline-500 animate-pulse shrink-0" />}
                  </div>
                  <div className="text-xs font-semibold truncate mt-0.5" style={{ color: t.brand?.color || "#e5342e" }}>{t.persona_name}</div>
                  {/* iter195w — portfolio linkage. Shows "in {Portfolio}" or a warning
                      chip when unassigned, so operators know the linkage state without
                      opening portfolio Overview. */}
                  <div data-testid={`community-card-portfolio-${t.id}`} className="text-[11px] mt-1 truncate">
                    {t.company_name ? (
                      <span className="inline-flex items-center gap-1 text-ink/45">
                        <Landmark className="h-3 w-3 text-ink/35" strokeWidth={2} />
                        in <span className="font-semibold text-ink/70">{t.company_name}</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-amber-700 font-semibold">
                        <AlertTriangle className="h-3 w-3" strokeWidth={2} /> Not in a portfolio
                      </span>
                    )}
                  </div>
                  <ManagedBy tenant={t} testid={`community-card-manager-${t.id}`} className="mt-0.5 max-w-full" />
                  <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                    <span className="text-[11px] font-medium text-ink/45 tabular-nums">{t.phone_number || "No number yet"}</span>
                    {urgent > 0 && (
                      <button data-testid={`urgent-badge-${t.id}`} onClick={() => enterCommunity(t.id, "/dashboard/actions")} title="Open urgent requests"
                        className="rounded-full bg-redline-600 text-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide hover:bg-redline-700 transition-colors">
                        {urgent} urgent
                      </button>
                    )}
                    {(t.stats?.pending_registrations || 0) > 0 && (
                      <button data-testid={`pending-regs-badge-${t.id}`} onClick={() => enterCommunity(t.id, "/dashboard/roster")} title="Residents awaiting your approval — open roster"
                        className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide hover:bg-amber-200 transition-colors">
                        {t.stats.pending_registrations} awaiting approval
                      </button>
                    )}
                    {t.status === "pending_activation" && (
                      <span data-testid={`pending-badge-${t.id}`} className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">Pending number</span>
                    )}
                    <TwilioStatusPill status={twilioStatus[t.id]} tid={t.id} />
                  </div>
                </div>
              </div>

              {/* compact inline stats — every stat is a deep link into that community */}
              <div className="px-4 pb-3 flex items-center gap-3 text-xs flex-wrap">
                <button data-testid={`stat-chats-${t.id}`} onClick={() => enterCommunity(t.id, "/dashboard/conversations")} title="Open text conversations (all time)"
                  className="flex items-center gap-1.5 text-ink/55 rounded-full px-2 py-1 -mx-1 hover:bg-sand-50 hover:text-ink transition-colors">
                  <MessagesSquare className="h-3.5 w-3.5 text-ink/30" strokeWidth={1.75} /><span className="font-bold text-ink tabular-nums">{t.stats?.texts ?? t.stats?.conversations ?? 0}</span> texts
                </button>
                <button data-testid={`stat-calls-${t.id}`} onClick={() => enterCommunity(t.id, "/dashboard/conversations?channel=voice")} title="Open phone-call conversations (all time)"
                  className="flex items-center gap-1.5 text-ink/55 rounded-full px-2 py-1 -mx-1 hover:bg-sand-50 hover:text-ink transition-colors">
                  <Phone className="h-3.5 w-3.5 text-ink/30" strokeWidth={1.75} /><span className="font-bold text-ink tabular-nums">{t.stats?.calls ?? 0}</span> calls
                </button>
                {flagged > 0 && (
                  <button data-testid={`stat-flagged-${t.id}`} onClick={() => enterCommunity(t.id, "/dashboard/conversations?status=escalated")} title="Open escalated conversations"
                    className="flex items-center gap-1.5 text-redline-600 rounded-full px-2 py-1 -mx-1 hover:bg-redline-50 transition-colors">
                    <AlertTriangle className="h-3.5 w-3.5 text-danger-500" strokeWidth={1.75} /><span className="font-bold tabular-nums">{flagged}</span> flagged
                  </button>
                )}
                <button data-testid={`stat-requests-${t.id}`} onClick={() => enterCommunity(t.id, "/dashboard/actions")} title="Open requests — tickets waiting on the office"
                  className="flex items-center gap-1.5 text-ink/55 rounded-full px-2 py-1 -mx-1 hover:bg-sand-50 hover:text-ink transition-colors">
                  <ClipboardList className="h-3.5 w-3.5 text-ink/30" strokeWidth={1.75} /><span className="font-bold text-ink tabular-nums">{t.stats?.open_requests || 0}</span> requests
                </button>
                <button data-testid={`unit-label-${t.id}`} onClick={() => enterCommunity(t.id, "/dashboard/roster")} title={`Open ${vt.lcs("person")}`}
                  className="flex items-center gap-1.5 text-ink/55 rounded-full px-2 py-1 -mx-1 hover:bg-sand-50 hover:text-ink transition-colors group/unit">
                  <Users className="h-3.5 w-3.5 text-ink/30" strokeWidth={1.75} /><span className="font-bold text-ink tabular-nums">{t.stats?.households || 0}</span>
                  <span data-testid={`unit-label-cycle-${t.id}`} onClick={(e) => { e.stopPropagation(); cycleUnit(t); }} title="Click to switch Homes / Units"
                    className="border-b border-dashed border-transparent group-hover/unit:border-ink/40 lowercase">{unit}</span>
                </button>
                <span className="ml-auto"><PulseChip tenant={t} snap={pulseSnaps[t.id]} role={role} onOpen={() => enterCommunity(t.id, "/dashboard/pulse")} onTurnedOn={async () => { await refreshTenants(); reloadPulse(); }} /></span>
              </div>

              {/* details — collapsed by default, chevron reveals */}
              {isOpen && (
                <div className="border-t border-sand-100 px-4 py-3 space-y-3" data-testid={`community-details-${t.id}`}>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-ink/40 mb-1.5">Board members</div>
                    {bms.length === 0 ? (
                      <div className="text-sm text-ink/40">None added yet</div>
                    ) : (
                      <div className="space-y-1" data-testid={`board-members-${t.id}`}>
                        {bms.slice(0, 3).map((m, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm">
                            <UserRound className="h-3.5 w-3.5 text-ink/40 shrink-0" />
                            <span className="font-medium text-ink truncate">{m.name}</span>
                            {m.position && <span className="text-ink/50 truncate">· {m.position}</span>}
                          </div>
                        ))}
                        {bms.length > 3 && <div className="text-xs text-ink/50 pl-5">+{bms.length - 3} more</div>}
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-ink/40 mb-1.5">Modules</div>
                    <div className="flex flex-wrap gap-1.5" data-testid={`community-modules-${t.id}`}>
                      <ModulePill on icon={MessageSquareText} label="Concierge" />
                      <ModulePill on={(t.mode || "answers_requests") === "answers_requests"} icon={SlidersHorizontal} label="Requests" />
                      <ModulePill on={!!t.support_module?.enabled} icon={Headset} label="Concierge Team" />
                      <ModulePill on={!!t.security_module?.enabled} icon={ShieldAlert} label="Security" />
                      <ModulePill on={t.features?.voice !== false && !!t.phone_number} icon={Megaphone} label="Voice" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button data-testid={`configure-community-${t.id}`} onClick={() => enterCommunity(t.id, "/dashboard/settings?tab=service")}
                      className="rounded-xl bg-white border border-sand-200 text-ink/80 hover:bg-sand-50 hover:border-sand-300 transition-colors px-3 py-2 text-sm font-semibold flex items-center justify-center gap-1.5">
                      <SlidersHorizontal className="h-4 w-4" strokeWidth={1.75} /> Configure
                    </button>
                    <button data-testid={`invite-residents-${t.id}`} onClick={() => setPoster(t)}
                      className="rounded-xl bg-white border border-sand-200 text-ink/80 hover:bg-sand-50 hover:border-sand-300 transition-colors px-3 py-2 text-sm font-semibold flex items-center justify-center gap-1.5">
                      <Users className="h-4 w-4" strokeWidth={1.75} /> Invite
                    </button>
                  </div>
                </div>
              )}

              {/* actions */}
              <div className="mt-auto p-3 flex gap-2 border-t border-sand-100">
                <button data-testid={`manage-community-${t.id}`} onClick={() => enterCommunity(t.id)}
                  className="flex-1 rounded-xl bg-ink text-white hover:bg-redline-500 transition-colors font-semibold px-4 py-2 text-sm flex items-center justify-center gap-1.5 active:scale-[0.98]">
                  Manage community <ArrowRight className="h-4 w-4" strokeWidth={2} />
                </button>
                <button data-testid={`expand-community-${t.id}`} onClick={() => toggleCard(t.id)} aria-expanded={isOpen}
                  title={isOpen ? "Hide details" : "Board members, modules & more"}
                  className="w-10 rounded-xl bg-white border border-sand-200 text-ink/50 hover:text-ink hover:border-sand-300 transition-colors flex items-center justify-center">
                  <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} strokeWidth={2} />
                </button>
              </div>
            </div>
          );
        })}
        {list.length === 0 && (
          <div className="col-span-full text-center py-16 text-ink/40" data-testid="communities-empty">
            {tenants.length === 0 ? "No communities yet — create your first one."
              : flaggedOnly && !q.trim() && !pendingOnly && !regsOnly ? "All clear — no communities are flagged right now."
              : regsOnly && !q.trim() && !pendingOnly && !flaggedOnly ? "No residents are awaiting approval right now."
              : "No communities match your search / filter."}
          </div>
        )}
      </div>
      )}

      {/* Delete community — super-admin, typed confirmation */}
      <Dialog open={!!delTarget} onOpenChange={(v) => { if (!v) { setDelTarget(null); setDelText(""); setDelRelease(false); } }}>
        <DialogContent className="max-w-md" data-testid="delete-community-dialog">
          <DialogHeader><DialogTitle className="font-heading text-danger-600 flex items-center gap-2"><Trash2 className="h-5 w-5" /> Delete community</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-ink-700">
              This permanently deletes <span className="font-bold">{delTarget?.name}</span> and <span className="font-semibold">all of its data</span> —
              residents, conversations, documents, requests, amenities, surveys and settings. This <span className="font-semibold">cannot be undone</span>.
            </p>
            {delTarget?.phone_number && (
              <div data-testid="delete-community-number-choice" className="rounded-xl border border-sand-200 bg-sand-50 p-3 space-y-2">
                <div className="text-xs font-semibold text-ink">
                  Its texting number <span className="font-mono">{delTarget.phone_number}</span> — what should happen to it?
                </div>
                {[["keep", "Keep it on the Twilio account", "Unassigned and ready for another community. It keeps billing (about $1.15/mo for a local number) until you release it from Platform → Numbers."],
                  ["release", "Release it back to Twilio", "Billing stops now. The number is gone for good — Twilio can re-sell it and nobody can text or call it again."]].map(([val, title, note]) => (
                  <label key={val} data-testid={`delete-community-number-${val}`}
                    className={`flex items-start gap-2.5 rounded-lg border p-2.5 cursor-pointer transition-colors ${(delRelease ? "release" : "keep") === val ? "border-ink bg-white" : "border-transparent hover:bg-white/60"}`}>
                    <input type="radio" name="del-number" checked={(delRelease ? "release" : "keep") === val} onChange={() => setDelRelease(val === "release")}
                      className="mt-0.5 h-4 w-4 accent-redline-500" />
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-ink">{title}</span>
                      <span className="block text-xs text-ink/60 mt-0.5">{note}</span>
                    </span>
                  </label>
                ))}
              </div>
            )}
            <div>
              <label className="text-sm font-semibold">Type the community name to confirm</label>
              <input data-testid="delete-community-confirm-input" value={delText} onChange={(e) => setDelText(e.target.value)}
                placeholder={delTarget?.name || ""} autoFocus
                className="mt-1 w-full rounded-xl border border-sand-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-redline-500/30" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => { setDelTarget(null); setDelText(""); setDelRelease(false); }}
              className="rounded-full px-5 py-2.5 font-semibold text-ink-700 hover:bg-sand-100 transition-colors">Cancel</button>
            <button data-testid="delete-community-confirm-btn" onClick={doDelete}
              disabled={deleting || delText.trim() !== (delTarget?.name || "")}
              className="rounded-full bg-redline-500 text-white font-semibold px-6 py-2.5 hover:bg-redline-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              {deleting ? "Deleting…" : "Delete forever"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">New {wv.lc("org")} · Step {step} of 5</DialogTitle></DialogHeader>
          <div className="flex gap-1.5 mb-2">
            {[1, 2, 3, 4, 5].map((s) => <div key={s} className={`h-1.5 flex-1 rounded-full ${s <= step ? "bg-redline-500" : "bg-sand-200"}`} />)}
          </div>

          {step === 1 && (
            <div className="space-y-4" data-testid="wizard-step-1">
              <p className="text-sm text-muted-foreground">The basics for this {wv.lc("org")}.</p>
              <div>
                <label className="text-sm font-semibold">Industry</label>
                <div className="mt-1.5"><IndustryPicker packs={packs} value={industry} onChange={setIndustry} testid="community-industry" /></div>
                {pack?.intake ? <p className="text-xs text-muted-foreground mt-1.5" data-testid="community-industry-tagline">Also seeds {pack.intake} request categories for the concierge's intake.</p> : null}
              </div>
              {[["name", `${wv.one("org")} name`, pack?.examples?.name || (wv.isHoa ? "Maplewood Community HOA" : "Acme Roofing")],
                ["persona_name", "Assistant persona name", pack?.examples?.persona || (wv.isHoa ? "Ask Maplewood" : "Ask Acme")]].map(([k, l, ph]) => (
                <div key={k}>
                  <label className="text-sm font-semibold">{l}</label>
                  <input data-testid={`community-${k}-input`} value={basics[k]} onChange={(e) => setBasics({ ...basics, [k]: e.target.value })}
                    placeholder={ph} className="mt-1 w-full rounded-xl border border-sand-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-redline-500/30" />
                </div>
              ))}

              {/* Managed by — attach this community to a management company's portfolio (optional) */}
              <div>
                <label className="text-sm font-semibold">AI concierge model</label>
                <select data-testid="community-ai_model-select" value={basics.ai_model}
                  onChange={(e) => setBasics({ ...basics, ai_model: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-sand-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-redline-500/30">
                  <option value="auto">Auto — best balance (recommended)</option>
                  <option value="gpt-5.5">OpenAI GPT-5.5 — most natural</option>
                  <option value="gpt-5.4-mini">OpenAI GPT-5.4 mini — lowest cost</option>
                  <option value="claude-sonnet">Anthropic Claude Sonnet 4.6</option>
                  <option value="claude-haiku">Anthropic Claude Haiku 4.5 — low cost</option>
                  <option value="gemini-pro">Google Gemini 3.1 Pro</option>
                  <option value="gemini-flash">Google Gemini 3 Flash — low cost</option>
                </select>
                <p className="mt-1 text-xs text-muted-foreground">Which provider answers {wv.lcs("person")}. You can change this anytime in Settings → The Concierge, and compare costs on AI Usage.</p>
              </div>
              <div>
                <label className="text-sm font-semibold">Request number prefix <span className="font-normal text-muted-foreground">(optional)</span></label>
                <input data-testid="community-request_code-input" maxLength={5} value={basics.request_code}
                  onChange={(e) => setBasics({ ...basics, request_code: e.target.value.toUpperCase().replace(/[^A-Z]/g, "") })}
                  placeholder={(basics.name || "REQ").replace(/[^A-Za-z]/g, "").slice(0, 3).toUpperCase() || "REQ"}
                  className="mt-1 w-40 rounded-xl border border-sand-200 px-4 py-2.5 font-mono uppercase outline-none focus:ring-2 focus:ring-redline-500/30" />
                <p className="mt-1 text-xs text-muted-foreground">Requests will be numbered like <span className="font-mono font-semibold">{(basics.request_code || (basics.name || "REQ").replace(/[^A-Za-z]/g, "").slice(0, 3).toUpperCase() || "REQ")}-1042</span>.</p>
              </div>

              <div>
                <label className="text-sm font-semibold">Portfolio / managed by <span className="font-normal text-muted-foreground">(optional)</span></label>
                <select data-testid="community-company-select" value={basics.company_id || ""} onChange={(e) => setBasics({ ...basics, company_id: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-sand-200 px-4 py-2.5 bg-white outline-none focus:ring-2 focus:ring-redline-500/30">
                  <option value="">Direct / self-managed (no portfolio)</option>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <p className="mt-1 text-xs text-muted-foreground">The portfolio's admins see this {wv.lc("org")} in their dashboard.</p>
              </div>

              {/* Dedicated number — pick from the pool of available Twilio numbers, or enter manually */}
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold">Dedicated number <span className="font-normal text-muted-foreground">(optional)</span></label>
                  <div className="flex items-center gap-3">
                    {numSel.mode !== "pool" && <button type="button" data-testid="num-mode-pool" onClick={() => setNumSel({ phone_number: "", number_sid: "", mode: "pool" })}
                      className="text-xs font-semibold text-redline-600 hover:underline">Choose from available</button>}
                    {numSel.mode !== "buy" && <button type="button" data-testid="num-mode-buy" onClick={() => setNumSel({ phone_number: "", number_sid: "", mode: "buy" })}
                      className="text-xs font-semibold text-redline-600 hover:underline">Buy a new number</button>}
                    {numSel.mode !== "manual" && <button type="button" data-testid="num-mode-manual" onClick={() => setNumSel({ phone_number: "", number_sid: "", mode: "manual" })}
                      className="text-xs font-semibold text-redline-600 hover:underline">Enter manually (porting)</button>}
                  </div>
                </div>

                {numSel.mode === "buy" ? (
                  numSel.phone_number ? (
                    <div className="mt-1 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-900" data-testid="community-number-bought">
                      <b className="font-mono">{numSel.phone_number}</b> bought and wired — it's assigned to this {wv.lc("org")} when you create it.
                    </div>
                  ) : (
                    <div className="mt-1 rounded-xl border border-sand-200 p-3">
                      <BuyNumberPanel compact testid="community-buy-number" onBought={(d) => setNumSel({ phone_number: d.phone_number, number_sid: "", mode: "buy" })} />
                    </div>
                  )
                ) : numSel.mode === "pool" ? (
                  numbers === null ? (
                    <div className="mt-1 text-sm text-muted-foreground px-1 py-2.5">Loading numbers…</div>
                  ) : !numCfg ? (
                    <p className="mt-1 text-sm text-muted-foreground bg-sand-50 border border-sand-200 rounded-xl px-4 py-2.5">Twilio isn't connected yet. You can assign a number later in Numbers, or enter one manually.</p>
                  ) : (
                    <select data-testid="community-number-select" value={numSel.phone_number}
                      onChange={(e) => { const n = numbers.find((x) => x.phone_number === e.target.value); setNumSel({ phone_number: e.target.value, number_sid: n?.sid || "", mode: "pool" }); }}
                      className="mt-1 w-full rounded-xl border border-sand-200 px-4 py-2.5 bg-white outline-none focus:ring-2 focus:ring-redline-500/30">
                      <option value="">— Not assigned yet —</option>
                      {numbers.map((n) => (
                        <option key={n.sid} value={n.phone_number} disabled={!!n.assigned_tenant_id || !!n.protected}>
                          {n.phone_number}{n.protected ? ` — live in production (${n.webhook_host}), locked here` : n.assigned_tenant_id ? ` — in use (${n.assigned_tenant_name})` : " — available"}
                        </option>
                      ))}
                    </select>
                  )
                ) : (
                  <input data-testid="community-number-manual" value={numSel.phone_number} onChange={(e) => setNumSel({ ...numSel, phone_number: e.target.value, number_sid: "", mode: "manual" })}
                    placeholder="+1512..." className="mt-1 w-full rounded-xl border border-sand-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-redline-500/30" />
                )}

                {numSel.phone_number.trim() ? (
                  <p className="mt-2 text-xs text-emerald-700 flex items-center gap-1.5"><Check className="h-3.5 w-3.5" /> {numSel.mode === "pool" && numSel.number_sid ? "We'll wire this number's webhook automatically on create." : "Number saved — webhook must be set once it's on your Twilio account."}</p>
                ) : (
                  <p className="mt-2 text-xs text-amber-700 flex items-center gap-1.5" data-testid="num-not-live-badge"><AlertTriangle className="h-3.5 w-3.5" /> No live number yet — {wv.lcs("person")} can't text this {wv.lc("org")} until you assign one.</p>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-1" data-testid="wizard-step-2">
              <p className="text-sm text-muted-foreground">Look &amp; feel — the colour and logo {wv.lcs("person")} see on their portal, sign-in and public page, and the {wv.isHoa ? "door" : wv.lc("unit")} count every report divides by. All optional; edit anytime in Community Profile.</p>
              <div>
                <label className="text-sm font-semibold">{wv.isHoa ? "Total properties" : `Total ${wv.lcs("unit")} served`} <span className="font-normal text-muted-foreground">{wv.isHoa ? "— doors in the association" : `— active ${wv.lcs("person")} or ${wv.lcs("household")}`}</span></label>
                <input data-testid="wizard-unit-count" type="number" min={0} max={100000} value={look.unit_count}
                  onChange={(e) => setLook({ ...look, unit_count: e.target.value })} placeholder="e.g. 240"
                  className="mt-1 w-full rounded-xl border border-sand-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-redline-500/30 tabular-nums" />
              </div>
              <div>
                <label className="text-sm font-semibold">Brand colour</label>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {BRAND_PRESETS.map(([hex, label]) => {
                    const on = (look.brand_color || "#D32F2F").toLowerCase() === hex.toLowerCase();
                    return (
                      <button key={hex} type="button" title={label} data-testid={`wizard-color-${hex.slice(1).toLowerCase()}`}
                        onClick={() => setLook({ ...look, brand_color: hex === "#D32F2F" ? "" : hex })}
                        className={`h-8 w-8 rounded-full border-2 transition-transform ${on ? "border-ink scale-110" : "border-white shadow"}`}
                        style={{ backgroundColor: hex }} />
                    );
                  })}
                  <input data-testid="wizard-color-hex" value={look.brand_color} onChange={(e) => setLook({ ...look, brand_color: e.target.value.trim() })}
                    placeholder="#1B7F4B" maxLength={7}
                    className="w-28 rounded-xl border border-sand-200 px-3 py-1.5 text-sm font-mono outline-none focus:ring-2 focus:ring-redline-500/30" />
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">Leave on Redline red to keep the default look.</p>
              </div>
              <div>
                <label className="text-sm font-semibold">Logo <span className="font-normal text-muted-foreground">(optional)</span></label>
                <div className="mt-2 flex items-center gap-3">
                  <div className="h-14 w-14 rounded-xl border border-sand-200 bg-white flex items-center justify-center overflow-hidden shrink-0">
                    {look.logo_preview
                      ? <img src={look.logo_preview} alt="" className="h-full w-full object-contain p-1" data-testid="wizard-logo-preview" />
                      : <span className="font-heading font-extrabold text-lg" style={{ color: look.brand_color || "#D32F2F" }}>{(basics.name.trim()[0] || "?").toUpperCase()}</span>}
                  </div>
                  <label className="rounded-full border border-sand-200 hover:border-ink px-4 py-2 text-sm font-semibold cursor-pointer transition-colors" data-testid="wizard-logo-upload">
                    {logoBusy ? "Uploading…" : look.logo_path ? "Replace logo" : "Upload logo"}
                    <input type="file" accept="image/*" className="hidden" onChange={uploadWizardLogo} disabled={logoBusy} />
                  </label>
                  {look.logo_path && (
                    <button type="button" data-testid="wizard-logo-remove" onClick={() => setLook({ ...look, logo_path: "", logo_preview: "" })}
                      className="text-sm font-semibold text-ink/60 hover:text-danger-600">Remove</button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">PNG or SVG on a transparent background works best. Under 4 MB.</p>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-1" data-testid="wizard-step-3">
              <p className="text-sm text-muted-foreground">
                Every module the platform has. Most are on unless the {pack?.name || "industry"} pack says otherwise; Website Knowledge and the portal start off. Names follow the pack — change any of this later in Modules.
              </p>
              <ModuleGrid title="Core modules" items={CORE_MODULES} state={features} onChange={(k, v) => setFeatures({ ...features, [k]: v })} wv={wv} prefix="wizard-feature" />
              <ModuleGrid title="Add-ons" hint={`Premium — billed per ${wv.lc("org")}`} items={ADDON_MODULES} state={addons} onChange={(k, v) => setAddons({ ...addons, [k]: v })} wv={wv} prefix="wizard-addon" />
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4 max-h-[50vh] overflow-y-auto" data-testid="wizard-step-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold">{wv.one("person")} disclosure &amp; consent</span>
                <Switch data-testid="wizard-disclosure-enabled" checked={disc.enabled} onCheckedChange={(v) => setDisc({ ...disc, enabled: v })} />
              </div>
              <textarea data-testid="wizard-disclosure-text" rows={4} value={disc.text} onChange={(e) => setDisc({ ...disc, text: e.target.value })} disabled={!disc.enabled}
                className="w-full rounded-xl border border-sand-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30 disabled:bg-sand-50" />
              <div>
                <label className="text-sm font-semibold">Blocked topics (comma-separated)</label>
                <input data-testid="wizard-blocked-topics" value={guard.blocked_topics} onChange={(e) => setGuard({ ...guard, blocked_topics: e.target.value })}
                  placeholder="politics, legal disputes" className="mt-1 w-full rounded-xl border border-sand-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
              </div>
              <div>
                <label className="text-sm font-semibold">Blocked words (comma-separated)</label>
                <input data-testid="wizard-blocked-words" value={guard.blocked_words} onChange={(e) => setGuard({ ...guard, blocked_words: e.target.value })}
                  placeholder="lawsuit, attorney" className="mt-1 w-full rounded-xl border border-sand-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-3 max-h-[50vh] overflow-y-auto" data-testid="wizard-step-5">
              <p className="text-sm text-muted-foreground">Invite the {wv.role("hoa_manager").toLowerCase()}(s) and {wv.lcs("board")} now — or skip and do it later from Team. {wv.many("board")} get a one-tap magic-link welcome email.</p>
              {invites.map((inv, idx) => (
                <div key={idx} className="flex gap-2 items-start rounded-xl border border-sand-200 p-3" data-testid={`invite-row-${idx}`}>
                  <div className="flex-1 space-y-2">
                    <input data-testid={`invite-email-${idx}`} value={inv.email} type="email" placeholder="email@example.com"
                      onChange={(e) => setInvites(invites.map((x, i) => i === idx ? { ...x, email: e.target.value } : x))}
                      className="w-full rounded-lg border border-sand-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
                    <div className="flex gap-2">
                      <input data-testid={`invite-name-${idx}`} value={inv.name} placeholder="Full name (optional)"
                        onChange={(e) => setInvites(invites.map((x, i) => i === idx ? { ...x, name: e.target.value } : x))}
                        className="flex-1 rounded-lg border border-sand-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
                      <select data-testid={`invite-role-${idx}`} value={inv.role}
                        onChange={(e) => setInvites(invites.map((x, i) => i === idx ? { ...x, role: e.target.value } : x))}
                        className="rounded-lg border border-sand-200 px-3 py-2 text-sm bg-white outline-none">
                        <option value="hoa_manager">{wv.role("hoa_manager")}</option>
                        <option value="board_member">{wv.role("board_member")}</option>
                      </select>
                    </div>
                  </div>
                  {invites.length > 1 && <button data-testid={`invite-remove-${idx}`} onClick={() => setInvites(invites.filter((_, i) => i !== idx))} className="mt-0.5 h-8 w-8 rounded-lg text-red-500 hover:bg-red-50 flex items-center justify-center shrink-0"><X className="h-4 w-4" /></button>}
                </div>
              ))}
              <button data-testid="invite-add-row" onClick={() => setInvites([...invites, { email: "", name: "", role: "board_member" }])}
                className="text-sm font-semibold text-redline-600 hover:underline flex items-center gap-1"><Plus className="h-4 w-4" /> Add another person</button>
            </div>
          )}

          <div className="flex justify-between pt-2">
            <button data-testid="wizard-back" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1}
              className="rounded-full px-5 py-2.5 text-sm font-semibold border border-sand-200 hover:border-ink transition-colors disabled:opacity-40">Back</button>
            {step < 5 ? (
              <button data-testid="wizard-next" onClick={() => {
                  if (step === 1 && !basics.name.trim()) { toast.error(`${wv.one("org")} name required`); return; }
                  if (step === 2 && look.brand_color && !/^#[0-9a-fA-F]{6}$/.test(look.brand_color)) { toast.error("Brand colour must be a hex value like #1B7F4B"); return; }
                  setStep((s) => s + 1);
                }}
                className="rounded-full bg-ink text-white px-6 py-2.5 text-sm font-semibold hover:bg-ink-700 transition-colors">Next</button>
            ) : (
              <button data-testid="wizard-finish" onClick={finish} disabled={saving}
                className="rounded-full bg-redline-500 text-white px-6 py-2.5 text-sm font-semibold hover:bg-redline-600 transition-colors flex items-center gap-2 disabled:opacity-60">
                <Check className="h-4 w-4" /> {saving ? "Creating…" : "Create & open"}
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <InviteFlyerDialog community={poster} onClose={() => setPoster(null)} />
    </div>
  );
}
